import { useEffect, useState } from "react";
import { Search, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button-variants";
import { useNavigate } from "react-router-dom";
import { specialtyMatches } from "@/lib/searchUtils";
import { gtmPush } from "@/lib/gtmTracking";
import { supabase } from "@/integrations/supabase/client";

// Repli si la base n'est pas joignable ; la liste réelle vient de la table metiers
const FALLBACK_SPECIALTIES = [
  "Anesthésiste-Réanimateur",
  "Cardiologue",
  "Chirurgien orthopédique",
  "Généraliste",
  "Gériatre",
  "Gynécologue",
  "Pédiatre",
  "Psychiatre",
  "Radiologue",
  "Urgentiste",
];

const SearchBar = () => {
  const navigate = useNavigate();
  const [specialty, setSpecialty] = useState("");
  const [location, setLocation] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [specialties, setSpecialties] = useState<string[]>(FALLBACK_SPECIALTIES);

  // Les suggestions doivent reprendre exactement les noms de spécialités
  // utilisés par les offres, sinon la recherche aboutit à 0 résultat.
  useEffect(() => {
    let cancelled = false;

    const loadSpecialties = async () => {
      try {
        const { data, error } = await supabase
          .from("metiers")
          .select("title")
          .eq("is_active", true)
          .order("title", { ascending: true });

        if (error) throw error;
        const titles = (data || []).map((row: { title: string }) => row.title).filter(Boolean);
        if (!cancelled && titles.length > 0) setSpecialties(titles);
      } catch (error) {
        console.error("Error loading specialties:", error);
      }
    };

    loadSpecialties();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (specialty) params.set("specialty", specialty);
    if (location) params.set("location", location);

    // GA4 — event standard "site_search" (nom recommandé par Google)
    gtmPush({
      event: "site_search",
      search_term: specialty || "",
      search_location: location || "",
      search_has_specialty: !!specialty,
      search_has_location: !!location,
      click_location: "section",
    });

    navigate(`/offres?${params.toString()}`);
  };

  const filteredSpecialties = specialties
    .filter((s) => specialtyMatches(s, specialty))
    .slice(0, 8);

  return (
    <>
      <div className="grid md:grid-cols-2 gap-3 mb-6">
        {/* Specialty field with autocomplete */}
        <div className="relative group">
          <div className="flex items-center bg-muted/30 border-2 border-border rounded-xl px-4 py-3 focus-within:border-primary focus-within:bg-white focus-within:shadow-md transition-all duration-300">
            <Search className="w-5 h-5 text-primary mr-3 transition-transform group-focus-within:scale-110" />
            <input
              type="text"
              placeholder="Spécialité"
              value={specialty}
              onChange={(e) => setSpecialty(e.target.value)}
              onFocus={() => setShowSuggestions(true)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
              className="flex-1 outline-none text-base bg-transparent placeholder:text-muted-foreground font-medium"
              aria-label="Recherche par spécialité"
            />
          </div>
          {showSuggestions && specialty && filteredSpecialties.length > 0 && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-white border-2 border-primary/20 rounded-xl shadow-xl z-10 max-h-64 overflow-y-auto animate-fade-in">
              {filteredSpecialties.map((s, index) => (
                <button
                  key={s}
                  onClick={() => {
                    setSpecialty(s);
                    setShowSuggestions(false);
                    gtmPush({
                      event: "search_suggestion_click",
                      search_term: s,
                      click_location: "section",
                    });
                  }}
                  className="w-full text-left px-4 py-3 hover:bg-primary/5 transition-colors border-b border-border last:border-b-0 font-medium"
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  {s}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Location field */}
        <div className="group">
          <div className="flex items-center bg-muted/30 border-2 border-border rounded-xl px-4 py-3 focus-within:border-primary focus-within:bg-white focus-within:shadow-md transition-all duration-300">
            <MapPin className="w-5 h-5 text-primary mr-3 transition-transform group-focus-within:scale-110" />
            <input
              type="text"
              placeholder="Lieu"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              className="flex-1 outline-none text-base bg-transparent placeholder:text-muted-foreground font-medium"
              aria-label="Recherche par lieu"
            />
          </div>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 mb-4">
        <Button 
          variant="cta" 
          size="lg" 
          className="flex-1 sm:flex-initial py-4 sm:py-3 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 group"
          onClick={handleSearch}
          aria-label="Rechercher des missions médicales"
          data-cta="search_jobs"
          data-cta-destination="/offres"
        >
          Rechercher
          <Search className="w-4 h-4 ml-2 group-hover:rotate-90 transition-transform duration-300" />
        </Button>
        <Button 
          variant="outline" 
          size="lg" 
          className="flex-1 sm:flex-initial py-4 sm:py-3 border-2 hover:shadow-md transition-all duration-300 hover:scale-105"
          onClick={() => navigate("/offres")}
          aria-label="Voir toutes les offres d'emploi"
        >
          Voir toutes les offres
        </Button>
      </div>

    </>
  );
};

export default SearchBar;
